import { useEffect, useState, Fragment } from 'react'
import { useDispatch } from 'react-redux'

import IngestionFrequency from './IngestionFrequency'
import InfoTooltip from '../../../Shared/InfoTooltip'
import { Flex, Text, Button, Slot, Toggle } from '@/primitives'
import { Dialog } from '@/elements'

import {
  sendUpdateZoneMaintenanceMode,
  sendUpdateTrialModeView
} from '@/slices/management/zone'
import { sendUpdateSiteTrackingStatus } from '@/slices/ml/siteTracker'
import { getCurrentUser, getSiteTrackerStatus } from '@/reducers/selectors'

import Strings from '../../Strings'
import {
  getIsGodMode,
  findPermissionById,
  RESOURCE_TYPE_ZONE,
  getPermissionsList
} from '@/Util/PermissionUtils'
import { isZoneSite } from '@/Util/AdminUtils'
import ZoneUtils from '@/Util/ZoneUtils'

const SettingsModal = ({ selectedZone }) => {
  const strings = Strings()

  const dispatch = useDispatch()

  const coretexUser = getCurrentUser()
  const siteTrackerStatus = getSiteTrackerStatus()
  const isGodMode = getIsGodMode(coretexUser)

  const [showModal, setShowModal] = useState(false)
  const [isAiForecastEnabled, setIsAiForecastEnabled] = useState(false)
  const [isTrialModeViewEnabled, setIsTrialModeViewEnabled] = useState(false)
  const [isMaintenanceModeEnabled, setIsMaintenanceModeEnabled] =
    useState(false)

  const isSite = selectedZone?.id ? isZoneSite(selectedZone) : false

  useEffect(() => {
    setIsMaintenanceModeEnabled(selectedZone?.maintenanceMode)
  }, [selectedZone?.maintenanceMode])

  useEffect(() => {
    setIsTrialModeViewEnabled(selectedZone?.trialModeView)
  }, [selectedZone?.trialModeView])

  useEffect(() => {
    setIsAiForecastEnabled(siteTrackerStatus?.tracking)
  }, [siteTrackerStatus])

  const permissionsList = getPermissionsList(
    coretexUser,
    RESOURCE_TYPE_ZONE,
    'write'
  )

  const hasZoneEditPermission =
    findPermissionById(
      permissionsList,
      ZoneUtils.getPrimaryZoneFromParentPath(selectedZone?.parentPath)
    ) ||
    findPermissionById(permissionsList) ||
    isGodMode

  const onToggleModal = () => {
    setShowModal(!showModal)
  }

  function onClickAiForecastToggle() {
    setIsAiForecastEnabled(!isAiForecastEnabled)
    dispatch(
      sendUpdateSiteTrackingStatus({
        siteId: selectedZone.id,
        tracking: !isAiForecastEnabled
      })
    )
  }
  function onClickTrialModeViewToggle() {
    const newMode = !isTrialModeViewEnabled
    setIsTrialModeViewEnabled(newMode)
    dispatch(
      sendUpdateTrialModeView({
        id: selectedZone.id,
        trialModeView: newMode
      })
    )
  }

  function onClickMaintenanceModaToggle() {
    const newMode = !isMaintenanceModeEnabled
    setIsMaintenanceModeEnabled(newMode)
    dispatch(
      sendUpdateZoneMaintenanceMode({
        id: selectedZone.id,
        maintenanceMode: newMode
      })
    )
  }

  const {
    enableAiForecast,
    enableMaintenanceMode,
    editSettings,
    enableTrialModeView
  } = strings
  const showButton = hasZoneEditPermission || (isSite && isGodMode)

  return (
    <Fragment>
      {showButton && (
        <Button size='small' variant='primary' onClick={onToggleModal}>
          {editSettings}
        </Button>
      )}
      {showModal && (
        <Dialog
          open={showModal}
          onOpenChange={setShowModal}
          type='offcanvas'
          className='ZoneDetails__OffCanvas'
          style={{ zIndex: 3 }}
        >
          <Slot name='title'>
            <Text size={300} fontWeight={700}>
              {editSettings}
            </Text>
          </Slot>
          <Slot name='content'>
            <Flex
              direction='column'
              axisGap={500}
              alignMainAxis='flex-start'
              style={{ marginTop: '2em' }}
            >
              {hasZoneEditPermission && (
                <Flex
                  direction='column'
                  alignMainAxis='flex-start'
                  axisGap={300}
                >
                  <Text as='p' size={300} fontWeight='700'>
                    {enableMaintenanceMode}
                  </Text>
                  <Toggle
                    checked={isMaintenanceModeEnabled}
                    disabled={!hasZoneEditPermission}
                    indeterminate={false}
                    onChange={onClickMaintenanceModaToggle}
                  />
                </Flex>
              )}
              {isSite && isGodMode && (
                <Flex
                  direction='column'
                  alignMainAxis='flex-start'
                  axisGap={300}
                >
                  <InfoTooltip tipId='lno7vf7d4d'>
                    {/* This tooltip has been added as an example only */}
                    <Text as='p' size={300} fontWeight='700'>
                      {enableAiForecast}
                    </Text>
                  </InfoTooltip>
                  <Toggle
                    checked={isAiForecastEnabled}
                    disabled={!isSite || !isGodMode}
                    indeterminate={false}
                    onChange={onClickAiForecastToggle}
                  />
                </Flex>
              )}
              {isSite && hasZoneEditPermission && (
                <Flex
                  direction='column'
                  alignMainAxis='flex-start'
                  axisGap={300}
                >
                  <Text as='p' size={300} fontWeight='700'>
                    {enableTrialModeView}
                  </Text>
                  <Toggle
                    checked={isTrialModeViewEnabled}
                    disabled={!isSite || !isGodMode}
                    indeterminate={false}
                    onChange={onClickTrialModeViewToggle}
                  />
                </Flex>
              )}
            </Flex>
            {isSite && isGodMode && <IngestionFrequency />}
          </Slot>
        </Dialog>
      )}
    </Fragment>
  )
}

export default SettingsModal
